import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

export default function LoginPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate("/home", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-arena-base noise-overlay flex items-center justify-center px-6">
      {/* Glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[400px] bg-arena-blue/[0.06] rounded-full blur-[120px] pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-sm"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <div className="w-10 h-10 bg-arena-blue/20 border border-arena-blue/40 rounded-md flex items-center justify-center relative overflow-hidden">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-arena-blue relative z-10">
              <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="font-heading font-bold text-white text-xl tracking-tight leading-none">
              QA Arena
            </span>
            <a
              href="https://www.questbugs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[9px] font-mono text-zinc-500 hover:text-zinc-400 transition-colors leading-none mt-0.5"
            >
              by QuestBugs
            </a>
          </div>
        </div>

        {/* Card */}
        <div className="bg-arena-surface border border-white/[0.06] rounded-md p-8">
          <h1 className="font-heading font-semibold text-white text-xl text-center mb-2">
            Sign in to QA Arena
          </h1>
          <p className="text-sm font-mono text-zinc-500 text-center mb-8">
            Build, benchmark, and deploy QA agents.
          </p>

          <Button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            className="w-full h-11 bg-white text-black hover:bg-zinc-200 font-mono text-sm rounded-md gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" className="shrink-0">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.26c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-[11px] font-mono text-zinc-600 text-center mt-6 leading-relaxed">
            By signing in, you agree to our{" "}
            <a href="/terms" className="text-zinc-400 hover:text-white transition-colors underline underline-offset-2">Terms</a>{" "}
            and{" "}
            <a href="/privacy" className="text-zinc-400 hover:text-white transition-colors underline underline-offset-2">Privacy Policy</a>.
          </p>
        </div>

        <p className="text-[11px] font-mono text-zinc-600 text-center mt-6">
          <a href="/" className="hover:text-zinc-400 transition-colors">
            Back to home
          </a>
        </p>
      </motion.div>
    </div>
  );
}
